import { useState } from 'react';
import { api } from '../lib/api.js';

export function AddEhrForm({
  onCreated,
  onCancel,
}: {
  onCreated: (ehrId: string) => void;
  onCancel?: () => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function slugify(name: string) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { ehr } = await api.createEhr({
        slug: slugify(displayName),
        displayName,
        loginUrl,
      });
      onCreated(ehr.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="EHR Name" hint="e.g. Elation, Healthie, Charm">
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="EHR name"
          className="w-full bg-bg-primary border border-border rounded px-3 py-1.5 text-sm"
          required
        />
      </Field>

      <Field label="Login URL" hint="The URL of the EHR login page">
        <input
          type="url"
          value={loginUrl}
          onChange={(e) => setLoginUrl(e.target.value)}
          placeholder="https://app.example.com/login"
          className="w-full bg-bg-primary border border-border rounded px-3 py-1.5 text-sm font-mono"
          required
        />
      </Field>

      {error && (
        <div className="rounded border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--accent-red)', background: 'var(--accent-red)11', color: 'var(--accent-red)' }}>
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        {onCancel && (
          <button type="button" onClick={onCancel}
            className="text-sm text-text-secondary hover:text-text-primary px-3 py-1.5">
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
        >
          {submitting ? 'Adding…' : 'Add EHR'}
        </button>
      </div>
    </form>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-text-secondary mb-1">{label}</div>
      {children}
      {hint && <div className="text-xs text-text-dimmed mt-1">{hint}</div>}
    </label>
  );
}
